<template>
  <div class="application-list">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-office-building"></i>
              门店申请管理
            </h1>
            <p class="page-subtitle">管理门店入驻申请与审核流程</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button 
            type="primary" 
            icon="el-icon-refresh" 
            @click="refreshAllData"
            :loading="loading"
            size="large"
            class="refresh-btn"
          >
            刷新数据
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 统计卡片 -->
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-icon pending">
            <i class="el-icon-time"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.pending || 0 }}</div>
            <div class="stat-label">待审核</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon approved">
            <i class="el-icon-success"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.approved || 0 }}</div>
            <div class="stat-label">已通过</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon rejected">
            <i class="el-icon-error"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.rejected || 0 }}</div>
            <div class="stat-label">已拒绝</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon total">
            <i class="el-icon-s-data"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.total || 0 }}</div>
            <div class="stat-label">总申请数</div>
          </div>
        </div>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-section">
        <div class="filter-card">
          <el-form :model="searchForm" :inline="true" class="filter-form">
            <div class="form-row">
              <div class="form-item">
                <label class="form-label">申请状态</label>
                <el-select 
                  v-model="searchForm.status" 
                  placeholder="全部状态" 
                  clearable
                  size="large"
                >
                  <el-option label="待审核" :value="0" />
                  <el-option label="已通过" :value="1" />
                  <el-option label="已拒绝" :value="2" />
                </el-select>
              </div>
              
              <div class="form-item">
                <label class="form-label">申请类型</label>
                <el-select 
                  v-model="searchForm.store_type" 
                  placeholder="全部类型" 
                  clearable
                  size="large"
                >
                  <el-option label="门店" :value="1" />
                  <el-option label="代理" :value="2" />
                </el-select>
              </div>
              
              <div class="form-item">
                <label class="form-label">开始日期</label>
                <el-date-picker
                  v-model="searchForm.start_date"
                  type="date"
                  placeholder="请选择日期"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  size="large"
                />
              </div>
              
              <div class="form-item">
                <label class="form-label">结束日期</label>
                <el-date-picker
                  v-model="searchForm.end_date"
                  type="date"
                  placeholder="请选择日期"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  size="large"
                />
              </div>
            </div>
            
            <div class="form-actions">
              <el-button type="primary" @click="handleSearch" size="large">
                <i class="el-icon-search"></i> 搜索
              </el-button>
              <el-button @click="resetSearch" size="large">
                <i class="el-icon-refresh-left"></i> 重置
              </el-button>
            </div>
          </el-form>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-card">
          <div class="table-header">
            <h3>
              <i class="el-icon-menu"></i>
              申请列表
            </h3>
            <p>共 {{ pagination.total }} 条记录</p>
          </div>

          <el-table
            v-loading="loading"
            :data="applicationList"
            style="width: 100%"
            class="application-table"
          >
            <el-table-column prop="id" label="申请ID" width="80" align="center">
              <template slot-scope="scope">
                <span class="application-id">#{{ scope.row.id }}</span>
              </template>
            </el-table-column>

            <el-table-column label="申请信息" min-width="200">
              <template slot-scope="scope">
                <div class="application-info">
                  <div class="store-name">{{ scope.row.store_name }}</div>
                  <div class="applicant-info">
                    <span class="applicant">{{ scope.row.applicant_name }}</span>
                    <span class="phone">{{ scope.row.applicant_phone }}</span>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="类型" width="100" align="center">
              <template slot-scope="scope">
                <el-tag 
                  :type="scope.row.store_type === 1 ? 'primary' : 'success'"
                  class="store-type-tag"
                >
                  {{ scope.row.store_type === 1 ? '门店' : '代理' }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="地址" min-width="200">
              <template slot-scope="scope">
                <div class="address-info">
                  <i class="el-icon-location"></i>
                  <span class="address-text">{{ scope.row.address }}</span>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="状态" width="120" align="center">
              <template slot-scope="scope">
                <div class="status-wrapper">
                  <el-tag 
                    :class="getStatusClass(scope.row.status)"
                    class="status-tag"
                  >
                    <i :class="getStatusIcon(scope.row.status)"></i>
                    {{ getStatusText(scope.row.status) }}
                  </el-tag>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="时间信息" min-width="160">
              <template slot-scope="scope">
                <div class="time-info">
                  <div class="time-item">
                    <i class="el-icon-time"></i>
                    申请时间
                  </div>
                  <div class="time-value">{{ formatDate(scope.row.created_at) }}</div>
                  <div v-if="scope.row.audit_time" class="time-item audit">
                    <i class="el-icon-check"></i>
                    审核时间
                  </div>
                  <div v-if="scope.row.audit_time" class="time-value">{{ formatDate(scope.row.audit_time) }}</div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="180" fixed="right" align="center">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button 
                    type="primary" 
                    icon="el-icon-view" 
                    size="mini" 
                    @click="handleViewDetail(scope.row)"
                    circle
                    class="action-btn detail-btn"
                  />
                  <el-button
                    v-if="scope.row.status === 0"
                    type="success"
                    icon="el-icon-check"
                    size="mini"
                    @click="handleAudit(scope.row, 1)"
                    circle
                    class="action-btn approve-btn"
                  />
                  <el-button
                    v-if="scope.row.status === 0"
                    type="danger"
                    icon="el-icon-close"
                    size="mini"
                    @click="handleAudit(scope.row, 2)"
                    circle
                    class="action-btn reject-btn"
                  />
                </div>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 分页 -->
          <div class="pagination-wrapper">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="totalCount"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- 申请详情对话框 -->
    <el-dialog
      title="申请详情"
      :visible.sync="detailDialogVisible"
      width="700px"
      class="detail-dialog"
    >
      <div v-if="currentApplication" class="application-detail">
        <div class="detail-header">
          <div class="application-status">
            <el-tag 
              :class="getStatusClass(currentApplication.status)"
              size="large"
              class="status-tag-large"
            >
              <i :class="getStatusIcon(currentApplication.status)"></i>
              {{ getStatusText(currentApplication.status) }}
            </el-tag>
          </div>
          <div class="application-id-large">#{{ currentApplication.id }}</div>
        </div>

        <div class="detail-content">
          <div class="detail-section">
            <h4><i class="el-icon-info"></i> 基本信息</h4>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="门店名称">{{ currentApplication.store_name }}</el-descriptions-item>
              <el-descriptions-item label="申请类型">
                <el-tag :type="currentApplication.store_type === 1 ? 'primary' : 'success'">
                  {{ currentApplication.store_type === 1 ? '门店' : '代理' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="申请人">{{ currentApplication.applicant_name }}</el-descriptions-item>
              <el-descriptions-item label="联系电话">{{ currentApplication.applicant_phone }}</el-descriptions-item>
              <el-descriptions-item label="省份">{{ currentApplication.province || '-' }}</el-descriptions-item>
              <el-descriptions-item label="城市">{{ currentApplication.city || '-' }}</el-descriptions-item>
              <el-descriptions-item label="详细地址" :span="2">{{ currentApplication.address || '-' }}</el-descriptions-item>
            </el-descriptions>
          </div>

          <div class="detail-section" v-if="currentApplication.application_reason">
            <h4><i class="el-icon-document"></i> 申请理由</h4>
            <div class="reason-content">
              {{ currentApplication.application_reason }}
            </div>
          </div>

          <div class="detail-section">
            <h4><i class="el-icon-time"></i> 时间信息</h4>
            <div class="time-grid">
              <div class="time-card">
                <i class="el-icon-time"></i>
                <div class="time-info-detail">
                  <div class="time-label">申请时间</div>
                  <div class="time-value">{{ formatDate(currentApplication.created_at) }}</div>
                </div>
              </div>
              <div v-if="currentApplication.audit_time" class="time-card audit">
                <i class="el-icon-check"></i>
                <div class="time-info-detail">
                  <div class="time-label">审核时间</div>
                  <div class="time-value">{{ formatDate(currentApplication.audit_time) }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="currentApplication.audit_reason" class="detail-section">
            <h4><i class="el-icon-edit-outline"></i> 审核意见</h4>
            <div class="audit-reason">
              {{ currentApplication.audit_reason }}
            </div>
          </div>
          
          <!-- 证件图片 -->
          <div v-if="hasImages" class="detail-section">
            <h4><i class="el-icon-picture"></i> 证件图片</h4>
            <div class="images-grid">
              <div v-if="businessLicenseUrl" class="image-item">
                <div class="image-label">
                  <i class="el-icon-office-building"></i>
                  营业执照
                </div>
                <div class="image-wrapper">
                  <img :src="businessLicenseUrl" alt="营业执照" @click="previewImage(businessLicenseUrl)" style="cursor: pointer;" />
                  <div class="image-overlay" @click="previewImage(businessLicenseUrl)">
                    <i class="el-icon-zoom-in"></i>
                  </div>
                </div>
              </div>
              <div v-if="idCardFrontUrl" class="image-item">
                <div class="image-label">
                  <i class="el-icon-postcard"></i>
                  身份证正面
                </div>
                <div class="image-wrapper">
                  <img :src="idCardFrontUrl" alt="身份证正面" @click="previewImage(idCardFrontUrl)" style="cursor: pointer;" />
                  <div class="image-overlay" @click="previewImage(idCardFrontUrl)">
                    <i class="el-icon-zoom-in"></i>
                  </div>
                </div>
              </div>
              <div v-if="idCardBackUrl" class="image-item">
                <div class="image-label">
                  <i class="el-icon-postcard"></i>
                  身份证背面
                </div>
                <div class="image-wrapper">
                  <img :src="idCardBackUrl" alt="身份证背面" @click="previewImage(idCardBackUrl)" style="cursor: pointer;" />
                  <div class="image-overlay" @click="previewImage(idCardBackUrl)">
                    <i class="el-icon-zoom-in"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false" size="large">关闭</el-button>
        <el-button
          v-if="currentApplication && currentApplication.status === 0"
          type="success"
          @click="handleAudit(currentApplication, 1)"
          size="large"
        >
          <i class="el-icon-check"></i> 通过
        </el-button>
        <el-button
          v-if="currentApplication && currentApplication.status === 0"
          type="danger"
          @click="handleAudit(currentApplication, 2)"
          size="large"
        >
          <i class="el-icon-close"></i> 拒绝
        </el-button>
      </div>
    </el-dialog>
    
    <!-- 图片预览对话框 -->
    <el-dialog
      title="图片预览"
      :visible.sync="imagePreviewVisible"
      width="80%"
      class="preview-dialog"
      :close-on-click-modal="true"
    >
      <div class="image-preview">
        <img :src="previewImageUrl" alt="图片预览" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getStoreApplicationList, getStoreApplication, auditStoreApplication } from '@/api/store'
import { processImageUrl } from '@/utils/config'

export default {
  name: 'StoreApplicationList',
  data() {
    return {
      loading: false,
      applicationList: [],
      statistics: {
        pending: 0,
        approved: 0,
        rejected: 0,
        total: 0
      },
      pagination: {
        current_page: 1,
        page_size: 20,
        total: 0,
        total_pages: 0
      },
      // 分页相关的响应式数据
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      searchForm: {
        status: null,
        store_type: null,
        start_date: '',
        end_date: ''
      },
      detailDialogVisible: false,
      imagePreviewVisible: false,
      currentApplication: null,
      previewImageUrl: ''
    }
  },
  computed: {
    hasImages() {
      return this.businessLicenseUrl || this.idCardFrontUrl || this.idCardBackUrl
    },
    
    businessLicenseUrl() {
      if (!this.currentApplication) return null
      return this.currentApplication.business_license_image || this.currentApplication.business_license
    },
    
    idCardFrontUrl() {
      if (!this.currentApplication) return null
      return this.currentApplication.id_card_front_image || this.currentApplication.id_card_front
    },
    
    idCardBackUrl() {
      if (!this.currentApplication) return null
      return this.currentApplication.id_card_back_image || this.currentApplication.id_card_back
    }
  },
  async mounted() {
    // 并行加载申请列表和统计数据
    await Promise.all([
      this.loadApplicationList(),
      this.loadStatistics()
    ])
  },
  methods: {
    async loadApplicationList() {
      try {
        this.loading = true
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
          ...this.searchForm
        }
        
        // 过滤空值
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === '') {
            delete params[key]
          }
        })
        
        const res = await getStoreApplicationList(params)
        // 处理申请列表中的图片URL
        this.applicationList = (res.data.list || []).map(application => ({
          ...application,
          business_license_image: processImageUrl(application.business_license_image),
          id_card_front_image: processImageUrl(application.id_card_front_image),
          id_card_back_image: processImageUrl(application.id_card_back_image)
        }))
        
        // 更新分页数据
        if (res.data.pagination) {
          this.totalCount = res.data.pagination.total || 0
          this.pagination.total = res.data.pagination.total || 0
          this.pagination.total_pages = res.data.pagination.total_pages || 0
          this.pagination.page_size = res.data.pagination.page_size || this.pageSize
          // 同步到老的pagination对象以保持兼容性
          this.pagination.current_page = this.currentPage
        }
      } catch (error) {
        console.error('获取申请列表失败:', error)
        this.$message.error('获取申请列表失败')
      } finally {
        this.loading = false
      }
    },

    async loadStatistics() {
      try {
        // 获取基础搜索参数（不包含status，因为我们要分别统计各状态）
        const baseParams = {
          store_type: this.searchForm.store_type,
          start_date: this.searchForm.start_date,
          end_date: this.searchForm.end_date
        }
        
        // 清理空值
        Object.keys(baseParams).forEach(key => {
          if (baseParams[key] === null || baseParams[key] === '') {
            delete baseParams[key]
          }
        })
        
        // 并行获取各状态的统计数据
        const [totalRes, pendingRes, approvedRes, rejectedRes] = await Promise.all([
          // 获取总数
          getStoreApplicationList({ ...baseParams, page_size: 1 }),
          // 获取待审核数量
          getStoreApplicationList({ ...baseParams, status: 0, page_size: 1 }),
          // 获取已通过数量
          getStoreApplicationList({ ...baseParams, status: 1, page_size: 1 }),
          // 获取已拒绝数量
          getStoreApplicationList({ ...baseParams, status: 2, page_size: 1 })
        ])
        
        this.statistics = {
          total: totalRes.data?.pagination?.total || 0,
          pending: pendingRes.data?.pagination?.total || 0,
          approved: approvedRes.data?.pagination?.total || 0,
          rejected: rejectedRes.data?.pagination?.total || 0
        }
      } catch (error) {
        console.error('加载统计数据失败:', error)
        // 发生错误时重置统计数据
        this.statistics = {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0
        }
      }
    },
    
    // 刷新所有数据
    async refreshAllData() {
      await Promise.all([
        this.loadApplicationList(),
        this.loadStatistics()
      ])
      this.$message.success('数据已刷新')
    },
    
    async handleSearch() {
      this.currentPage = 1
      this.pagination.current_page = 1
      // 并行加载申请列表和统计数据
      await Promise.all([
        this.loadApplicationList(),
        this.loadStatistics()
      ])
    },
    
    async resetSearch() {
      this.searchForm = {
        status: null,
        store_type: null,
        start_date: '',
        end_date: ''
      }
      this.currentPage = 1
      this.pagination.current_page = 1
      // 并行加载申请列表和统计数据
      await Promise.all([
        this.loadApplicationList(),
        this.loadStatistics()
      ])
    },

    
    handleSizeChange(val) {
      this.pageSize = val
      this.pagination.page_size = val
      this.currentPage = 1
      this.pagination.current_page = 1
      this.loadApplicationList()
    },
    
    handleCurrentChange(val) {
      this.currentPage = val
      this.pagination.current_page = val
      this.loadApplicationList()
    },
    
    async handleViewDetail(row) {
      try {
        const res = await getStoreApplication(row.id)
        
        // 处理详情中的图片URL - 兼容不同的字段名
        this.currentApplication = {
          ...res.data,
          // 兼容两种可能的字段名格式
          business_license_image: processImageUrl(res.data.business_license_image || res.data.business_license),
          id_card_front_image: processImageUrl(res.data.id_card_front_image || res.data.id_card_front),
          id_card_back_image: processImageUrl(res.data.id_card_back_image || res.data.id_card_back)
        }
        this.detailDialogVisible = true
      } catch (error) {
        console.error('获取申请详情失败:', error)
        this.$message.error('获取申请详情失败')
      }
    },
    
    async handleAudit(application, status) {
      try {
        const action = status === 1 ? '通过' : '拒绝'
        const { value } = await this.$prompt('请输入审核意见', `${action}申请`, {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPlaceholder: status === 1 ? '请输入审核意见（可选）' : '请输入拒绝原因',
          inputValidator: (value) => {
            // 通过申请时，审核意见为可选；拒绝申请时，必须填写原因
            if (status === 2 && !value) {
              return '拒绝申请时必须输入拒绝原因'
            }
            return true
          }
        })
        
        await auditStoreApplication(application.id, {
          status,
          audit_reason: value
        })
        
        this.$message.success(`申请${action}成功`)
        this.detailDialogVisible = false
        // 刷新当前页面数据和统计数据
        await Promise.all([
          this.loadApplicationList(),
          this.loadStatistics()
        ])
      } catch (error) {
        if (error !== 'cancel') {
          console.error('审核失败:', error)
          this.$message.error('审核失败')
        }
      }
    },
    
    previewImage(imageUrl) {
      if (!imageUrl) {
        this.$message.warning('图片URL为空，无法预览')
        return
      }
      this.previewImageUrl = processImageUrl(imageUrl)
      this.imagePreviewVisible = true
    },
    
    getStatusText(status) {
      const statusMap = {
        0: '待审核',
        1: '已通过',
        2: '已拒绝'
      }
      return statusMap[status] || '未知'
    },
    
    getStatusClass(status) {
      const classMap = {
        0: 'status-pending',
        1: 'status-approved',
        2: 'status-rejected'
      }
      return classMap[status] || 'status-unknown'
    },

    getStatusIcon(status) {
      const iconMap = {
        0: 'el-icon-time',
        1: 'el-icon-success',
        2: 'el-icon-error'
      }
      return iconMap[status] || 'el-icon-info'
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.application-list {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-info {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title i {
  font-size: 32px;
}

.page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

/* 主内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 统计卡片 */
.stats-section {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.stat-icon.pending::before {
  background: linear-gradient(135deg, #f39c12, #e67e22);
}

.stat-icon.pending i {
  color: #f39c12;
}

.stat-icon.approved::before {
  background: linear-gradient(135deg, #43e97b, #38f9d7);
}

.stat-icon.approved i {
  color: #43e97b;
}

.stat-icon.rejected::before {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.stat-icon.rejected i {
  color: #f093fb;
}

.stat-icon.total::before {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

.stat-icon.total i {
  color: #4facfe;
}

.stat-icon i {
  z-index: 1;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 30px;
}

.filter-card {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
}

.filter-form {
  margin: 0;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-item {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

.form-actions {
  text-align: center;
}

.form-actions .el-button {
  min-width: 120px;
  margin: 0 10px;
}

/* 表格区域 */
.table-section {
  margin-bottom: 30px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
}

.table-header {
  padding: 25px 30px 15px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #fafbfc, #f8f9fa);
}

.table-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 5px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-header h3 i {
  color: #667eea;
  font-size: 20px;
}

.table-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* 表格样式 */
.application-table :deep(.el-table) {
  border: none;
}

.application-table :deep(.el-table th) {
  background: #fafbfc;
  border: none;
  color: #606266;
  font-weight: 600;
}

.application-table :deep(.el-table td) {
  border: none;
  padding: 15px 10px;
}

.application-table :deep(.el-table--border::after) {
  display: none;
}

.application-table :deep(.el-table::before) {
  display: none;
}

.application-id {
  font-weight: 600;
  color: #667eea;
  font-family: monospace;
}

.application-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.store-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.applicant-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.applicant {
  font-size: 14px;
  color: #666;
}

.phone {
  font-size: 13px;
  color: #999;
  font-family: monospace;
}

.store-type-tag {
  border-radius: 20px;
  padding: 5px 15px;
  font-weight: 500;
}

.address-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.address-info i {
  color: #909399;
  margin-top: 2px;
  flex-shrink: 0;
}

.address-text {
  font-size: 14px;
  color: #606266;
  line-height: 1.4;
}

.status-wrapper {
  display: flex;
  justify-content: center;
}

.status-tag {
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.status-tag.status-pending {
  background: #fdf6ec;
  color: #e6a23c;
  border-color: #f5dab1;
}

.status-tag.status-approved {
  background: #f0f9ff;
  color: #67c23a;
  border-color: #c2e7b0;
}

.status-tag.status-rejected {
  background: #fef0f0;
  color: #f56c6c;
  border-color: #fbc4c4;
}

.time-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-item {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.time-item.audit {
  color: #67c23a;
}

.time-value {
  font-size: 13px;
  color: #606266;
  font-family: monospace;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.detail-btn:hover {
  background: #667eea !important;
  border-color: #667eea !important;
  transform: scale(1.1);
}

.approve-btn:hover {
  background: #67c23a !important;
  border-color: #67c23a !important;
  transform: scale(1.1);
}

.reject-btn:hover {
  background: #f56c6c !important;
  border-color: #f56c6c !important;
  transform: scale(1.1);
}

/* 分页 */
.pagination-wrapper {
  padding: 20px 30px;
  text-align: right;
  background: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

/* 对话框样式 */
.detail-dialog :deep(.el-dialog) {
  border-radius: 16px;
  overflow: hidden;
}

.detail-dialog :deep(.el-dialog__header) {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 20px 25px;
}

.detail-dialog :deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
  font-size: 18px;
}

.detail-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
  color: rgba(255, 255, 255, 0.8);
  font-size: 20px;
}

.detail-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.application-detail {
  max-height: 70vh;
  overflow-y: auto;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  background: #fafbfc;
  border-bottom: 1px solid #f0f2f5;
}

.status-tag-large {
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-tag-large i {
  font-size: 18px;
}

.application-id-large {
  font-size: 24px;
  font-weight: 700;
  color: #667eea;
  font-family: monospace;
}

.detail-content {
  padding: 25px;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f2f5;
}

.detail-section h4 i {
  color: #667eea;
  font-size: 18px;
}

.reason-content {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  line-height: 1.6;
  color: #606266;
}

.time-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.time-card {
  background: #fafbfc;
  padding: 15px;
  border-radius: 10px;
  border: 1px solid #f0f2f5;
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-card i {
  font-size: 20px;
  color: #667eea;
}

.time-card.audit i {
  color: #67c23a;
}

.time-info-detail {
  flex: 1;
}

.time-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 4px;
}

.audit-reason {
  background: #fff7e6;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #f39c12;
  line-height: 1.6;
  color: #606266;
}

/* 图片网格 */
.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.image-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.image-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.image-label i {
  color: #667eea;
}

.image-wrapper {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.image-wrapper img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.image-wrapper:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  color: white;
  font-size: 24px;
}

/* 图片预览对话框 */
.preview-dialog :deep(.el-dialog) {
  border-radius: 16px;
  max-width: 95vw;
}

.preview-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.image-preview {
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
}

.image-preview img {
  max-width: 100%;
  max-height: 70vh;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  object-fit: contain;
}

/* 对话框底部 */
.dialog-footer {
  text-align: right;
  padding: 20px 25px;
  background: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .stats-section {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .form-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .main-content {
    padding: 0 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .time-grid {
    grid-template-columns: 1fr;
  }
  
  .images-grid {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 5px;
  }
}

/* Element UI 样式覆盖 */
:deep(.el-form-item) {
  margin-bottom: 0;
}

:deep(.el-input__inner) {
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}

:deep(.el-input__inner:focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

:deep(.el-select) {
  width: 100%;
}

:deep(.el-date-editor.el-input) {
  width: 100%;
}

:deep(.el-pagination) {
  text-align: right;
}
</style> 